import * as yup from "yup";
import { lineOptions } from "../../constants/lines";

export { lineOptions } from "../../constants/lines";
export type { Line } from "../../constants/lines";

export const panelId = "create-calculation-panel";

const currentYear = new Date().getUTCFullYear();

export const createCalculationSchema = yup.object({
  name: yup
    .string()
    .transform((v) => (typeof v === "string" ? v.trim() : v))
    .required("Name is required")
    .max(255, "Name is capped at 255 characters"),
  kamName: yup
    .string()
    .transform((v) => (typeof v === "string" ? v.trim() : v))
    .required("KAM name is required")
    .max(255, "KAM name is capped at 255 characters"),
  line: yup.mixed<(typeof lineOptions)[number]>().required(),
  planningYear: yup.number().integer().required(),
  supplierId: yup.string().required("Supplier is required"),
  // Submit-time backstop; message never renders (Step-2 Tip + disabled Next enforce >=1).
  subCategoryIds: yup
    .array()
    .of(yup.string().required())
    .required()
    .min(1, "Select at least one category with one subcategory")
    .default([]),
  // dimensionTypeId/dimensionValues are a single choice applied to every selected
  // subcategory; null dimensionTypeId means "None".
  dimensionTypeId: yup.string().nullable().default(null),
  dimensionValues: yup
    .array()
    .of(yup.string().required())
    .required()
    .default([]),
});

export type CreateCalculationFormData = yup.InferType<
  typeof createCalculationSchema
>;

export const defaultValues: CreateCalculationFormData = {
  name: "",
  kamName: "",
  line: lineOptions[0],
  planningYear: currentYear + 1,
  supplierId: "",
  subCategoryIds: [],
  dimensionTypeId: null as string | null,
  dimensionValues: [],
};

export const shouldAdvanceEpoch = (
  previousActive: boolean,
  nextActive: boolean,
): boolean => previousActive !== nextActive && nextActive;

interface DimensionSelectionPayload {
  dimensionTypeId: string | null;
  values: string[];
}

// The single Step-3 dimension choice is applied to EVERY selected subcategory,
// dimensionless ones included (by design). The two builders differ only in how they
// encode "None": manual sends {dimensionTypeId: null, values: []}; AI sends null.

/** Manual `CREATE_CALCULATION` `subCategories` — one non-null selection per selected sub. */
export const buildManualSubCategories = (
  data: Pick<
    CreateCalculationFormData,
    "subCategoryIds" | "dimensionTypeId" | "dimensionValues"
  >,
): {
  subCategoryId: string;
  dimensionSelection: DimensionSelectionPayload;
}[] => {
  const dimensionSelection: DimensionSelectionPayload = {
    dimensionTypeId: data.dimensionTypeId ?? null,
    values: data.dimensionTypeId == null ? [] : data.dimensionValues,
  };
  return data.subCategoryIds.map((subCategoryId) => ({
    subCategoryId,
    dimensionSelection,
  }));
};

/** AI `CREATE_DRAFT_CALCULATION` `subCategorySelections` — null selection for a "None" dimension. */
export const buildAiSubCategorySelections = (
  data: Pick<
    CreateCalculationFormData,
    "subCategoryIds" | "dimensionTypeId" | "dimensionValues"
  >,
): {
  subCategoryId: string;
  dimensionSelection: DimensionSelectionPayload | null;
}[] => {
  const dimensionSelection: DimensionSelectionPayload | null =
    data.dimensionTypeId == null
      ? null
      : { dimensionTypeId: data.dimensionTypeId, values: data.dimensionValues };
  return data.subCategoryIds.map((subCategoryId) => ({
    subCategoryId,
    dimensionSelection,
  }));
};

export interface CreateButtonState {
  createLoading: boolean;
  refDataLoading: boolean;
  subCategoryIds: string[];
  dimensionPicked: boolean;
  dimensionTypeId: string | null;
  dimensionValues: string[];
}

// The dimensionPicked flag distinguishes "picked None" from "not yet touched" —
// both leave dimensionTypeId null, so the value alone can't gate Create. When a
// real dimension type is picked, at least one value is required — otherwise
// Calculation.create builds a dimensioned calc with zero buckets (non-computable).
export const isCreateDisabled = ({
  createLoading,
  refDataLoading,
  subCategoryIds,
  dimensionPicked,
  dimensionTypeId,
  dimensionValues,
}: CreateButtonState): boolean =>
  createLoading ||
  refDataLoading ||
  subCategoryIds.length === 0 ||
  !dimensionPicked ||
  (dimensionTypeId !== null && dimensionValues.length === 0);

export interface MetadataStepReadiness {
  refDataLoading: boolean;
  name: string;
  kamName: string;
  supplierId: string;
}

export const isNextDisabledOnMetadataStep = ({
  refDataLoading,
  name,
  kamName,
  supplierId,
}: MetadataStepReadiness): boolean =>
  refDataLoading ||
  name.trim() === "" ||
  kamName.trim() === "" ||
  supplierId === "";

export interface SubcategoryStepReadiness {
  sections: string[];
  subCategoryIds: string[];
  subCategoryIdsByCategory: Record<string, string[]>;
}

export const isNextDisabledOnSubcategoryStep = ({
  sections,
  subCategoryIds,
  subCategoryIdsByCategory,
}: SubcategoryStepReadiness): boolean => {
  if (subCategoryIds.length === 0) return true;
  return sections.some(
    (categoryId) =>
      categoryId !== "" &&
      !(subCategoryIdsByCategory[categoryId] ?? []).some((id) =>
        subCategoryIds.includes(id),
      ),
  );
};

export interface AiDraftButtonState {
  aiEnabled: boolean;
  currentStep: number;
}

// The "Draft with AI" button mirrors "Create": both render together on step 3
// and share the same disabled gate (`isCreateDisabled`). Visibility depends only
// on the AI flag being on and being on the dimension step — not on the header
// being complete, so the two buttons appear and disappear as a pair.
export const shouldShowAiDraftButton = ({
  aiEnabled,
  currentStep,
}: AiDraftButtonState): boolean => aiEnabled && currentStep === 2;
